import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Use the deferred storage queue for persisting level progression data.
import { queueJsonStorageWrite } from '../persistence/storageQueue';

const PLAYER_LEVEL_KEY = 'dd_player_level_v1';
const PLAYER_XP_KEY = 'dd_player_xp_v1';
const LEVEL_MAX_HP_BONUS_KEY = 'dd_level_maxhp_bonus_v1';
const LEVEL_MAX_SP_BONUS_KEY = 'dd_level_maxsp_bonus_v1';

const LEVEL_UP_PULSE_MS = 520;
const XP_PER_CULTIST_WIN = 10;

function readStoredNumber(key, fallback, { min = 0, integer = true } = {}) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    let value = Number(parsed);

    if (!Number.isFinite(value)) return fallback;
    if (integer) value = Math.floor(value);
    if (Number.isFinite(min)) value = Math.max(min, value);

    return value;
  } catch {
    return fallback;
  }
}

function writeStoredNumber(key, value) {
  // Queue the value for asynchronous write. This avoids blocking the main
  // thread during render and coalesces rapid updates.
  queueJsonStorageWrite(key, value);
}

function getInitialDrawBonusFromLevel(level) {
  const lvl = Math.max(1, Math.floor(Number(level) || 1));
  if (lvl >= 10) return 2;
  if (lvl >= 5) return 1;
  return 0;
}

export function useLevelProgression({ onLevelUpRefillRequested } = {}) {
  const [playerLevel, setPlayerLevel] = useState(() =>
    readStoredNumber(PLAYER_LEVEL_KEY, 1, { min: 1 })
  );

  const [playerXp, setPlayerXp] = useState(() =>
    readStoredNumber(PLAYER_XP_KEY, 0, { min: 0 })
  );

  const [levelMaxHpBonus, setLevelMaxHpBonus] = useState(() =>
    readStoredNumber(LEVEL_MAX_HP_BONUS_KEY, 0, { min: 0 })
  );

  const [levelMaxSpBonus, setLevelMaxSpBonus] = useState(() =>
    readStoredNumber(LEVEL_MAX_SP_BONUS_KEY, 0, { min: 0 })
  );

  const [isLevelUpOpen, setIsLevelUpOpen] = useState(false);
  const [levelUpPulse, setLevelUpPulse] = useState(null);
  const [levelUpStep, setLevelUpStep] = useState('choice');

  const earnedDrawBonusThisLevelUpRef = useRef(false);
  const levelUpTimeoutRef = useRef(null);

  const xpToNextLevel = useMemo(() => {
    return Math.floor(
      10 * Math.pow(1.35, Math.max(0, (Number(playerLevel) || 1) - 1))
    );
  }, [playerLevel]);

  const levelCombatMods = useMemo(() => {
    const bonus = Math.max(0, (Number(playerLevel) || 1) - 1);

    return {
      turnExtraRandomDraw: bonus,
      turnExtraPlays: bonus,
      firstTurnExtraDraw: getInitialDrawBonusFromLevel(playerLevel),
    };
  }, [playerLevel]);

  const clearLevelUpCloseTimeout = useCallback(() => {
    if (levelUpTimeoutRef.current) {
      window.clearTimeout(levelUpTimeoutRef.current);
      levelUpTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearLevelUpCloseTimeout();
    };
  }, [clearLevelUpCloseTimeout]);

  useEffect(() => {
    writeStoredNumber(PLAYER_LEVEL_KEY, playerLevel);
  }, [playerLevel]);

  useEffect(() => {
    writeStoredNumber(PLAYER_XP_KEY, playerXp);
  }, [playerXp]);

  useEffect(() => {
    writeStoredNumber(LEVEL_MAX_HP_BONUS_KEY, levelMaxHpBonus);
  }, [levelMaxHpBonus]);

  useEffect(() => {
    writeStoredNumber(LEVEL_MAX_SP_BONUS_KEY, levelMaxSpBonus);
  }, [levelMaxSpBonus]);

  const openLevelUp = useCallback(() => {
    clearLevelUpCloseTimeout();
    setLevelUpPulse(null);
    setLevelUpStep('choice');
    earnedDrawBonusThisLevelUpRef.current = false;
    setIsLevelUpOpen(true);
  }, [clearLevelUpCloseTimeout]);

  useEffect(() => {
    if (isLevelUpOpen) return;

    const xp = Number(playerXp) || 0;
    const need = Number(xpToNextLevel) || 0;

    if (need <= 0) return;
    if (xp < need) return;

    openLevelUp();
  }, [isLevelUpOpen, openLevelUp, playerXp, xpToNextLevel]);

  const applyLevelUpChoice = useCallback(
    (choice) => {
      if (levelUpPulse) return;

      const pick = choice === 'sp' ? 'sp' : 'hp';
      const thresholdAtModalOpen = Math.max(0, Math.floor(Number(xpToNextLevel) || 0));

      setLevelUpPulse(pick);

      setPlayerXp((xp) =>
        Math.max(0, Math.floor((Number(xp) || 0) - thresholdAtModalOpen))
      );

      setPlayerLevel((level) => {
        const current = Math.max(1, Math.floor(Number(level) || 1));
        const next = current + 1;

        const earnedDrawBonus =
          getInitialDrawBonusFromLevel(next) > getInitialDrawBonusFromLevel(current);

        earnedDrawBonusThisLevelUpRef.current = earnedDrawBonus;

        if (earnedDrawBonus) {
          setLevelUpStep('drawBonus');
        }

        return next;
      });

      if (pick === 'hp') {
        setLevelMaxHpBonus((bonus) =>
          Math.max(0, Math.floor((Number(bonus) || 0) + 20))
        );
      } else {
        setLevelMaxSpBonus((bonus) =>
          Math.max(0, Math.floor((Number(bonus) || 0) + 20))
        );
      }

      onLevelUpRefillRequested?.();

      clearLevelUpCloseTimeout();
      levelUpTimeoutRef.current = window.setTimeout(() => {
        levelUpTimeoutRef.current = null;

        if (earnedDrawBonusThisLevelUpRef.current) {
          setLevelUpPulse(null);
          return;
        }

        setIsLevelUpOpen(false);
        setLevelUpPulse(null);
      }, LEVEL_UP_PULSE_MS);
    },
    [clearLevelUpCloseTimeout, levelUpPulse, onLevelUpRefillRequested, xpToNextLevel]
  );

  const continueAfterDrawBonus = useCallback(() => {
    clearLevelUpCloseTimeout();
    earnedDrawBonusThisLevelUpRef.current = false;
    setLevelUpPulse(null);
    setLevelUpStep('choice');
    setIsLevelUpOpen(false);
  }, [clearLevelUpCloseTimeout]);

  const awardCombatVictoryXp = useCallback(
    ({
      didSlayEnemy,
      nextSlainCount,
      floorCleansingTotal,
      tarotNoExp,
      tarotChainExpBonus,
    }) => {
      const total = Math.max(0, Math.floor(Number(floorCleansingTotal) || 0));
      const slain = Math.max(0, Math.floor(Number(nextSlainCount) || 0));
      const clearedFloor = didSlayEnemy && total > 0 && slain >= total;

      setPlayerXp((xp) => {
        let gain = XP_PER_CULTIST_WIN;

        if (tarotNoExp) {
          gain = 0;
        } else if (tarotChainExpBonus) {
          gain = XP_PER_CULTIST_WIN * Math.max(1, slain);

          if (clearedFloor) {
            gain += Math.max(0, Number(xpToNextLevel) || 0);
          }
        }

        return Math.max(0, Math.floor((Number(xp) || 0) + gain));
      });
    },
    [xpToNextLevel]
  );

  return {
    playerLevel,
    playerXp,
    xpToNextLevel,
    levelMaxHpBonus,
    levelMaxSpBonus,
    isLevelUpOpen,
    levelUpPulse,
    levelUpStep,
    levelCombatMods,
    applyLevelUpChoice,
    continueAfterDrawBonus,
    awardCombatVictoryXp,
  };
}
